import { Card, Chip, Dialog, Paper, Typography, Box, Slide } from '@mui/material';
import { createTheme, styled } from '@mui/material/styles';
import { fontFamilyDisplay, fontFamilyMono } from './fonts';

// ---- "Deep Signal" design tokens ----
// Grounded in what this tool actually watches over: cameras and systems reporting
// back from the field, like sonar returns from deep water. Dark mode is the ink of
// the depths; the accent is the bioluminescent cyan signal cutting through it.

const ink = {
  bg: '#060B14',
  surface: '#0E1626',
  surfaceElevated: '#16213A',
  border: '#24304A',
  textPrimary: '#EAF3FB',
  textSecondary: '#93A2BC',
  textDisabled: '#5E6C87',
};

const daylight = {
  bg: '#F4F7FA',
  surface: '#FFFFFF',
  surfaceElevated: '#EAF0F4',
  border: '#D8E1E8',
  textPrimary: '#0E1B2C',
  textSecondary: '#55647A',
  textDisabled: '#9AA7B8',
};

const signalCyanDark = { main: '#2FE4D6', light: '#7BF0E6', dark: '#1EA89C', contrastText: '#04211F' };
const signalCyanLight = { main: '#0C8599', light: '#4FD1DB', dark: '#075A66', contrastText: '#FFFFFF' };
const violetDark = { main: '#9B8CFA', light: '#C3B8FF', dark: '#6E59D9', contrastText: '#1B1442' };
const violetLight = { main: '#6E59D9', light: '#9B8CFA', dark: '#4B3AA6', contrastText: '#FFFFFF' };

const sharedTypography = {
  fontFamily: fontFamilyDisplay,
  h4: { fontWeight: 800, letterSpacing: '-0.02em' },
  h5: { fontWeight: 700, letterSpacing: '-0.015em' },
  h6: { fontWeight: 700, letterSpacing: '-0.01em' },
  body1: { fontSize: '0.875rem', lineHeight: 1.6 },
  body2: { fontSize: '0.8125rem', lineHeight: 1.55 },
  button: { fontWeight: 700, textTransform: 'none', letterSpacing: '0.01em' },
  // Not a standard MUI key — consumed directly as theme.typography.mono for
  // UUIDs, timestamps, and other data readouts.
  mono: { fontFamily: fontFamilyMono },
};

function buildComponents({ mode, tokens, accent }) {
  const isDark = mode === 'dark';
  return {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 20px',
          fontSize: '0.875rem',
          fontWeight: 700,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: isDark ? '0px 0px 0px 1px rgba(47,228,214,0.25)' : '0px 4px 10px rgba(12,133,153,0.12)',
          },
        },
        contained: {
          background: `linear-gradient(135deg, ${accent.main} 0%, ${accent.dark} 100%)`,
          color: accent.contrastText,
          '&:hover': {
            background: `linear-gradient(135deg, ${accent.dark} 0%, ${accent.main} 100%)`,
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': { borderWidth: '1.5px' },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          border: `1px solid ${tokens.border}`,
          boxShadow: isDark
            ? '0px 1px 2px rgba(0,0,0,0.4)'
            : '0px 1px 2px rgba(14,27,44,0.04)',
          transition: 'border-color 0.2s ease, transform 0.2s ease',
          '&:hover': {
            borderColor: isDark ? 'rgba(47,228,214,0.35)' : 'rgba(12,133,153,0.25)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '14px 20px',
          borderBottom: `1px solid ${tokens.border}`,
          fontSize: '0.875rem',
        },
        head: {
          fontWeight: 700,
          backgroundColor: tokens.surfaceElevated,
          color: tokens.textSecondary,
          fontSize: '0.72rem',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          border: `1px solid ${tokens.border}`,
          boxShadow: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 600,
          fontSize: '0.75rem',
          height: 24,
        },
        outlined: { borderWidth: '1.5px' },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: tokens.surface,
          borderRight: `1px solid ${tokens.border}`,
          boxShadow: 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.surface,
          color: tokens.textPrimary,
          boxShadow: 'none',
          borderBottom: `1px solid ${tokens.border}`,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          backgroundColor: tokens.surface,
          boxShadow: isDark
            ? '0px 24px 48px -12px rgba(0,0,0,0.6)'
            : '0px 24px 48px -12px rgba(14,27,44,0.18)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: accent.main },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: { root: { borderRadius: 8 } },
    },
    ...(isDark
      ? {
          MuiTypography: { styleOverrides: { root: { color: tokens.textPrimary } } },
          MuiListItemText: {
            styleOverrides: {
              primary: { color: tokens.textPrimary },
              secondary: { color: tokens.textSecondary },
            },
          },
          MuiListItemIcon: { styleOverrides: { root: { color: tokens.textSecondary } } },
          MuiDivider: { styleOverrides: { root: { borderColor: tokens.border } } },
        }
      : {}),
  };
}

// ---- Dashboard styled components ----

export const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'visible',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
  },
}));

export const MetricValue = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 800,
  lineHeight: 1.2,
  fontFamily: theme.typography.mono.fontFamily,
  color: theme.palette.text.primary,
  fontVariantNumeric: 'tabular-nums',
}));

export const MetricLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 700,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: '0.07em',
}));

export const StatusChip = styled(Chip)(({ theme, status }) => {
  const isDark = theme.palette.mode === 'dark';
  const colors = {
    active: { bg: isDark ? '#1C9A6C' : '#1C9A6C', color: '#FFFFFF' },
    expired: { bg: isDark ? '#FF6B6B' : '#D6483F', color: '#FFFFFF' },
    online: { bg: isDark ? '#1C9A6C' : '#1C9A6C', color: '#FFFFFF' },
    offline: { bg: '#7C879C', color: '#FFFFFF' },
    warning: { bg: isDark ? '#F2B84D' : '#B8790E', color: isDark ? '#2C2103' : '#FFFFFF' },
  };
  const color = colors[status] || colors.active;

  return {
    backgroundColor: color.bg,
    color: color.color,
    fontWeight: 700,
    '& .MuiChip-label': {
      paddingLeft: 12,
      paddingRight: 12,
    },
  };
});

export const GlassDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(14, 22, 38, 0.96)'
      : 'rgba(255, 255, 255, 0.97)',
    backdropFilter: 'blur(20px)',
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(47,228,214,0.12)' : 'rgba(12,133,153,0.1)'}`,
    boxShadow: theme.palette.mode === 'dark'
      ? '0px 24px 48px -12px rgba(0,0,0,0.65)'
      : '0px 24px 48px -12px rgba(14,27,44,0.2)',
  },
}));

// ---- Dashboard light/dark themes ----

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: signalCyanLight,
    secondary: violetLight,
    success: { main: '#1C9A6C', light: '#34D399', dark: '#0F7A54' },
    error: { main: '#D6483F', light: '#F0847B', dark: '#A83229' },
    warning: { main: '#B8790E', light: '#E0A53D', dark: '#8A5B08' },
    info: signalCyanLight,
    background: {
      default: daylight.bg,
      paper: daylight.surface,
      elevated: daylight.surfaceElevated,
    },
    text: {
      primary: daylight.textPrimary,
      secondary: daylight.textSecondary,
      disabled: daylight.textDisabled,
    },
    divider: daylight.border,
  },
  shape: { borderRadius: 10 },
  typography: sharedTypography,
  shadows: [
    'none',
    '0px 1px 2px rgba(14,27,44,0.04)',
    '0px 2px 4px rgba(14,27,44,0.05)',
    '0px 4px 10px rgba(14,27,44,0.06)',
    '0px 10px 20px rgba(14,27,44,0.08)',
    '0px 24px 48px rgba(14,27,44,0.14)',
    ...Array(19).fill('none'),
  ],
  components: buildComponents({ mode: 'light', tokens: daylight, accent: signalCyanLight }),
});

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: signalCyanDark,
    secondary: violetDark,
    success: { main: '#34D399', light: '#6EE7C9', dark: '#1C9A6C' },
    error: { main: '#FF6B6B', light: '#FF9B9B', dark: '#D6483F' },
    warning: { main: '#F2B84D', light: '#F7D08A', dark: '#B8790E' },
    info: signalCyanDark,
    background: {
      default: ink.bg,
      paper: ink.surface,
      elevated: ink.surfaceElevated,
    },
    text: {
      primary: ink.textPrimary,
      secondary: ink.textSecondary,
      disabled: ink.textDisabled,
    },
    divider: ink.border,
  },
  shape: { borderRadius: 10 },
  typography: sharedTypography,
  shadows: [
    'none',
    '0px 1px 2px rgba(0,0,0,0.4)',
    '0px 2px 4px rgba(0,0,0,0.45)',
    '0px 4px 10px rgba(0,0,0,0.5)',
    '0px 10px 20px rgba(0,0,0,0.55)',
    '0px 24px 48px rgba(0,0,0,0.6)',
    ...Array(19).fill('none'),
  ],
  components: buildComponents({ mode: 'dark', tokens: ink, accent: signalCyanDark }),
});

// ---- Login page theme + styled components ----

export const loginTheme = createTheme({
  palette: {
    primary: signalCyanLight,
    secondary: { main: '#D6483F', light: '#F0847B', dark: '#A83229' },
    background: {
      default: daylight.bg,
      paper: daylight.surface,
    },
    text: {
      primary: daylight.textPrimary,
      secondary: daylight.textSecondary,
    },
    error: { main: '#D6483F' },
    success: { main: '#1C9A6C' },
  },
  shape: { borderRadius: 10 },
  typography: {
    ...sharedTypography,
    h4: { fontWeight: 800, letterSpacing: '-0.02em' },
    h6: { fontWeight: 700 },
    body1: { fontSize: '0.95rem' },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          padding: '12px 24px',
          fontSize: '1rem',
          fontWeight: 700,
          boxShadow: 'none',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0px 8px 20px rgba(12,133,153,0.2)',
          },
        },
        contained: {
          background: `linear-gradient(135deg, ${signalCyanLight.main} 0%, ${signalCyanLight.dark} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${signalCyanLight.dark} 0%, ${signalCyanLight.main} 100%)`,
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': { borderWidth: '2px' },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            backgroundColor: daylight.surfaceElevated,
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: daylight.surface,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: signalCyanLight.main },
            },
            '&.Mui-focused': {
              backgroundColor: daylight.surface,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: signalCyanLight.main,
                borderWidth: '2px',
              },
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '20px',
          boxShadow: '0px 24px 48px -12px rgba(6,11,20,0.25)',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'none',
          fontWeight: 600,
          '&:hover': { textDecoration: 'underline' },
        },
      },
    },
  },
});

export const GradientBackground = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'radial-gradient(ellipse at top left, #0E2F3A 0%, #060B14 55%)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-20%',
    right: '-10%',
    width: '520px',
    height: '520px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(47,228,214,0.16) 0%, rgba(47,228,214,0) 70%)',
    animation: 'float 20s ease-in-out infinite',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-20%',
    left: '-10%',
    width: '620px',
    height: '620px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(155,140,250,0.12) 0%, rgba(155,140,250,0) 70%)',
    animation: 'float 25s ease-in-out infinite reverse',
  },
  '@keyframes float': {
    '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
    '50%': { transform: 'translate(-30px, 30px) scale(1.1)' },
  },
}));

export const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  borderRadius: '24px',
  width: '100%',
  background: 'rgba(255, 255, 255, 0.97)',
  backdropFilter: 'blur(24px)',
  boxShadow: '0px 24px 60px -12px rgba(6,11,20,0.45)',
  position: 'relative',
  zIndex: 1,
  border: '1px solid rgba(255,255,255,0.3)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
  '@keyframes slideUp': {
    from: { opacity: 0, transform: 'translateY(40px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  animation: 'slideUp 0.7s cubic-bezier(0.16, 1, 0.3, 1) both',
}));

export const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

export const StyledLogo = styled('img')(({ theme }) => ({
  width: 180,
  height: 'auto',
  marginBottom: theme.spacing(2),
  filter: 'drop-shadow(0px 4px 12px rgba(12,133,153,0.25))',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
  },
  [theme.breakpoints.down('sm')]: {
    width: 140,
  },
}));

// ---- Shared status color system ----
// Statuses: 'active' (green), 'expiring' (amber, <=30 days left), 'expired' (red), 'new' (cyan, registered <=7 days ago), 'offline' (slate)
export const statusColors = {
  active: { light: { bg: '#E5F5EE', color: '#0F7A54' }, dark: { bg: 'rgba(52,211,153,0.15)', color: '#34D399' } },
  expiring: { light: { bg: '#FBF0DD', color: '#8A5B08' }, dark: { bg: 'rgba(242,184,77,0.15)', color: '#F2B84D' } },
  expired: { light: { bg: '#FBEAE8', color: '#A83229' }, dark: { bg: 'rgba(255,107,107,0.15)', color: '#FF6B6B' } },
  new: { light: { bg: '#E3F4F6', color: '#075A66' }, dark: { bg: 'rgba(47,228,214,0.15)', color: '#2FE4D6' } },
  offline: { light: { bg: '#EAF0F4', color: '#55647A' }, dark: { bg: 'rgba(147,162,188,0.15)', color: '#93A2BC' } },
};

export const getStatusColorSx = (status, darkMode) => {
  const palette = statusColors[status] || statusColors.active;
  const { bg, color } = darkMode ? palette.dark : palette.light;
  return { bgcolor: bg, color, fontWeight: 700 };
};

// ---- Shared dialog entrance transition (matches the login page's slide-up feel) ----
export const dialogTransitionProps = {
  TransitionComponent: Slide,
  TransitionProps: { direction: 'up' },
};

// ---- Shared page-header accent gradient (used by PageHeader) ----
export const headerAccentGradient = 'linear-gradient(90deg, #2FE4D6 0%, #9B8CFA 100%)';
